import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useEffect, useState } from 'react';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { useFormItem } from '../../common/hooks/use-form-item';
import { InputType } from '../../common/hooks/use-form-item/types';
import { Interpolation } from '../../config/i18n/components/Interpolation';
import { OrganizationUsersRepository } from '../../organization/users/services/OrganizationUsersRepository';
import { ButtonBox, DangerButton, TertiaryButton } from '../components/Buttons';

type Props = {
  userId: number;
  username: string;
  onClosed: (userid: number | undefined) => void;
};

export const DeleteUserFlow = ({ userId, username, onClosed }: Props) => {
  const [userService] = useState(new OrganizationUsersRepository());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      userService.cancelAllRequests();
    };
  }, [userService]);

  const [name, nameComponent] = useFormItem({
    type: InputType.TYPE_TEXT,
    label: t('deleteUser.name.input'),
    value: '',
    validator: null,
    inputOptions: {
      autoFocus: true,
      disabled: loading,
      focusedLabel: t('deleteUser.name.focused'),
      showRequiredIndicator: true,
    },
  });

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await userService.deleteUser(userId);
      onClosed(userId);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  // Using a form validator results in the input being marked as invalid
  // while the user is typing. We manually check if the typed value equals
  // the project name to enable the submit button
  const canSubmit = !loading && name === username;

  return (
    <Dialog title={t('deleteUser.title')} onDismiss={() => onClosed(undefined)}>
      <DialogBody>
        <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          <Interpolation i18nKey="deleteUser.content" items={[{ name: 'color_gray', color: 'grey.700' }]} values={{ username }} />
          {nameComponent}
          <ButtonBox>
            <TertiaryButton disabled={loading} onClick={() => onClosed(undefined)}>
              {t('deleteUser.back')}
            </TertiaryButton>
            <DangerButton disabled={!canSubmit} type="submit">
              {t('deleteUser.submit')}
            </DangerButton>
          </ButtonBox>
        </Stack>
      </DialogBody>
    </Dialog>
  );
};
